import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronDown, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ManualOrderForm from "@/pages/admin/ManualOrderForm";
import OrderEditForm from "@/pages/admin/OrderEditForm";
import { OrderShippingInfo } from "@/components/admin/OrderShippingInfo";

const STATUSES = [
  { v: "awaiting_bank_payment", l: "Awaiting Bank Payment" },
  { v: "awaiting_freight_quote", l: "Awaiting Freight Quote" },
  { v: "paid_processing", l: "Paid / Processing" },
  { v: "shipped", l: "Shipped" },
  { v: "completed", l: "Completed" },
  { v: "cancelled", l: "Cancelled" },
];
const color = (s) => ({
  awaiting_bank_payment: "bg-accent text-steel", paid_processing: "bg-emerald-600",
  shipped: "bg-blue-600", completed: "bg-emerald-700", cancelled: "bg-slate-500",
}[s] || "bg-slate-500");

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [manual, setManual] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/orders", { params: filter === "all" ? {} : { status: filter } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/admin/products").then((r) => setProducts(r.data)); }, []);

  const changeStatus = async (order_number, status) => {
    try {
      await api.patch(`/admin/orders/${order_number}/status`, { status });
      toast.success("Order status updated — customer notified by email");
      load();
    } catch { toast.error("Could not update status"); }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Permanently delete order ${o.order_number}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/orders/${o.order_number}`); toast.success(`Order ${o.order_number} deleted`); setOpen(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete order"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-3xl font-extrabold">Orders</h1>
        <Button data-testid="new-manual-order" onClick={() => setManual(true)} className="bg-accent text-steel hover:bg-amber-600 font-semibold">
          <Plus size={16} className="mr-1" /> New Manual Order
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">Verify payments, edit any order detail, or raise an order manually for any stock level.</p>
      <div className="mb-4 w-56">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {orders.length === 0 && <div className="rounded-xl border bg-white p-8 text-center text-muted-foreground">No orders.</div>}
        {orders.map((o) => (
          <div key={o.order_number} data-testid={`order-${o.order_number}`} className="rounded-xl border bg-white">
            <button onClick={() => setOpen(open === o.order_number ? null : o.order_number)}
              className="w-full flex flex-wrap items-center gap-4 p-4 text-left">
              <span className="font-mono font-bold">{o.order_number}</span>
              <span className="flex-1 min-w-[150px]">{o.customer?.name} {o.customer?.company && `· ${o.customer.company}`}</span>
              <Badge className={`${color(o.status)} hover:${color(o.status)}`}>{o.status_label}</Badge>
              <span className="font-mono font-bold">{money(o.total)}</span>
              <span className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland" })}</span>
              <ChevronDown size={16} className={`transition-transform ${open === o.order_number ? "rotate-180" : ""}`} />
            </button>
            {open === o.order_number && (
              <div className="border-t p-4 grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Items</h4>
                    <Button data-testid={`edit-order-${o.order_number}`} size="sm" variant="outline" onClick={() => setEditing(o)}>
                      <Pencil size={13} className="mr-1" /> Edit Order
                    </Button>
                    <Button data-testid={`delete-order-${o.order_number}`} size="sm" variant="outline" onClick={() => removeOrder(o)} className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2">
                      <Trash2 size={13} className="mr-1" /> Delete
                    </Button>
                  </div>
                  {o.items.map((i) => (
                    <div key={i.sku} className="flex justify-between border-b py-1">
                      <span>{i.qty} × {i.name} <span className="font-mono text-xs text-muted-foreground">{i.sku}</span></span>
                      <span className="font-mono">{money(i.line_total)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2"><span>Subtotal</span><span className="font-mono">{money(o.subtotal)}</span></div>
                  <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{o.free_shipping ? "FREE" : money(o.shipping)}</span></div>
                  <div className="flex justify-between text-muted-foreground"><span>GST incl</span><span className="font-mono">{money(o.gst_included)}</span></div>
                  <div className="flex justify-between font-bold"><span>Total</span><span className="font-mono">{money(o.total)}</span></div>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Customer & Delivery</h4>
                  <p>{o.customer?.email} · {o.customer?.phone}</p>
                  <p className="text-muted-foreground mt-1">Billing: {o.billing_address?.line1}, {o.billing_address?.city} {o.billing_address?.postcode}</p>
                  <p className="text-muted-foreground">Delivery: {o.delivery_address?.line1}, {o.delivery_address?.city} {o.delivery_address?.postcode}</p>
                  <p className="mt-1">Payment: <strong>Bank Transfer</strong> · Ref <span className="font-mono">{o.payment?.reference}</span></p>
                  <div className="mt-3">
                    <label className="text-xs text-muted-foreground">Quick status change (sends customer email)</label>
                    <Select value={o.status} onValueChange={(v) => changeStatus(o.order_number, v)}>
                      <SelectTrigger data-testid={`change-status-${o.order_number}`} className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <OrderShippingInfo o={o} onChanged={load} />
                  <a href={`/invoice/${o.order_number}`} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-accent hover:underline text-sm">View / Print Tax Invoice →</a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={manual} onOpenChange={setManual}>
        <DialogContent data-testid="manual-order-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Raise Manual Order</DialogTitle></DialogHeader>
          <ManualOrderForm products={products} onDone={() => { setManual(false); load(); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-order-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Order {editing?.order_number}</DialogTitle></DialogHeader>
          {editing && <OrderEditForm order={editing} products={products} onDone={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
